import React from 'react';
import { render, screen, waitFor } from '@testing-library/react';
import userEvent from '@testing-library/user-event';

import ConfigProvider from 'components/ConfigProvider';
import { NotificationsPanel } from '@decisiv/ui-components';

import useUserAccess, { REMOVE, RESTORE } from './useUserAccess';

const ACTIVE = { id: 1, name: 'Account A', removedAt: null };
const REMOVED = { id: 2, name: 'Account B', removedAt: '2025-01-01T00:00:00Z' };

const USER = { id: 7, name: 'Abe Martinez' };
const OTHER_USER = { id: 8, name: 'Bea Nowak' };

const OTHER_ACTIVE = { id: 3, name: 'Account C', removedAt: null };

let hook;
let onChanged;

/**
 * The hook under a real component and a real NotificationsPanel, mirroring
 * EditUser's useAccountAccess test — mocking @decisiv/ui-components would leave
 * the toast half of the hook untested.
 */
function Probe() {
  hook = useUserAccess({ onChanged });

  return (
    <div>
      <span data-testid="open">{String(hook.isOpen)}</span>
      <span data-testid="action">{hook.action ?? ''}</span>
      <span data-testid="user">{hook.user?.id ?? ''}</span>
      <span data-testid="options">
        {hook.accountOptions.map((option) => option.value).join(',')}
      </span>
      <span data-testid="selected">{hook.selectedIds.join(',')}</span>
      <span data-testid="error">{hook.error ?? ''}</span>
      <button type="button" onClick={() => hook.open(REMOVE, USER)}>
        open remove
      </button>
      <button type="button" onClick={() => hook.open(RESTORE, USER)}>
        open restore
      </button>
      <button type="button" onClick={() => hook.open(REMOVE, OTHER_USER)}>
        open remove for the other user
      </button>
      <button type="button" onClick={() => hook.setSelectedIds([])}>
        clear selection
      </button>
      <button type="button" onClick={() => hook.setSelectedIds([1])}>
        choose account 1
      </button>
      <button type="button" onClick={() => hook.setSelectedIds([2])}>
        choose account 2
      </button>
      <button type="button" onClick={() => hook.close()}>
        close
      </button>
      <button type="button" onClick={() => hook.submit()}>
        submit
      </button>
    </div>
  );
}

const renderProbe = () =>
  render(
    <ConfigProvider>
      <Probe />
      <NotificationsPanel marginTop={4} zIndex={1000} />
    </ConfigProvider>,
  );

const respondWith = (...responses) => {
  global.fetch = jest.fn();
  responses.forEach(({ ok = true, status = 200, body = {} }) =>
    global.fetch.mockImplementationOnce(() =>
      Promise.resolve({ ok, status, json: async () => body }),
    ),
  );
};

const accountsLoaded = { body: { accountAccess: [ACTIVE, REMOVED] } };

const open = async (label = 'open remove') => {
  userEvent.click(screen.getByRole('button', { name: label }));
  await waitFor(() =>
    expect(screen.getByTestId('open')).toHaveTextContent('true'),
  );
};

beforeEach(() => {
  onChanged = jest.fn();
  document.head.innerHTML = '<meta name="csrf-token" content="a-token" />';
});

afterEach(() => {
  hook = undefined;
  document.head.innerHTML = '';
});

describe('management/Users useUserAccess', () => {
  it('starts closed', () => {
    respondWith();
    renderProbe();

    expect(screen.getByTestId('open')).toHaveTextContent('false');
  });

  describe('opening for a remove', () => {
    it('offers only the accounts the user still holds', async () => {
      respondWith(accountsLoaded);
      renderProbe();
      await open();

      expect(screen.getByTestId('options')).toHaveTextContent('1');
    });

    // A destructive action does not get a destructive default: preselecting
    // everything made the tags' x spare an account rather than remove it.
    it('preselects nothing', async () => {
      respondWith(accountsLoaded);
      renderProbe();
      await open();

      expect(screen.getByTestId('selected')).toHaveTextContent('');
    });

    it('carries the row so the modal can name the user', async () => {
      respondWith(accountsLoaded);
      renderProbe();
      await open();

      expect(screen.getByTestId('user')).toHaveTextContent('7');
    });
  });

  describe('opening for a restore', () => {
    it('offers only the accounts that were taken away', async () => {
      respondWith(accountsLoaded);
      renderProbe();
      await open('open restore');

      expect(screen.getByTestId('options')).toHaveTextContent('2');
    });
  });

  it('reports a failed fetch rather than opening empty and silent', async () => {
    global.fetch = jest.fn().mockRejectedValue(new Error('nope'));
    renderProbe();
    userEvent.click(screen.getByRole('button', { name: 'open remove' }));

    await waitFor(() =>
      expect(screen.getByTestId('error')).toHaveTextContent(
        'Something went wrong. Please try again.',
      ),
    );
  });

  describe('submitting', () => {
    const submitAfterOpen = async (label = 'open remove') => {
      respondWith(accountsLoaded, { body: { accountAccess: [] } });
      renderProbe();
      await open(label);
      userEvent.click(
        screen.getByRole('button', {
          name:
            label === 'open restore' ? 'choose account 2' : 'choose account 1',
        }),
      );
      userEvent.click(screen.getByRole('button', { name: 'submit' }));
    };

    it('posts the selected accounts', async () => {
      await submitAfterOpen();

      await waitFor(() =>
        expect(global.fetch).toHaveBeenLastCalledWith(
          '/management/users/7/remove_access',
          expect.objectContaining({
            method: 'POST',
            body: JSON.stringify({ account_ids: [1] }),
          }),
        ),
      );
    });

    it('posts to the restore path for a restore', async () => {
      await submitAfterOpen('open restore');

      await waitFor(() =>
        expect(global.fetch).toHaveBeenLastCalledWith(
          '/management/users/7/restore_access',
          expect.anything(),
        ),
      );
    });

    it('raises a toast', async () => {
      await submitAfterOpen();

      expect(
        await screen.findByText('Access removed successfully'),
      ).toBeInTheDocument();
    });

    it('closes', async () => {
      await submitAfterOpen();

      await waitFor(() =>
        expect(screen.getByTestId('open')).toHaveTextContent('false'),
      );
    });

    // The user may have moved to the other tab, so the list has to be re-read.
    it('tells the caller to refetch', async () => {
      await submitAfterOpen();

      await waitFor(() => expect(onChanged).toHaveBeenCalled());
    });
  });

  describe('a rejected submit', () => {
    const rejectAfterOpen = async () => {
      respondWith(accountsLoaded, {
        ok: false,
        status: 422,
        body: { errors: { base: ['Choose at least one account.'] } },
      });
      renderProbe();
      await open();
      userEvent.click(screen.getByRole('button', { name: 'choose account 1' }));
      userEvent.click(screen.getByRole('button', { name: 'submit' }));
    };

    it('shows the server reason', async () => {
      await rejectAfterOpen();

      await waitFor(() =>
        expect(screen.getByTestId('error')).toHaveTextContent(
          'Choose at least one account.',
        ),
      );
    });

    it('stays open so the selection can be narrowed and retried', async () => {
      await rejectAfterOpen();

      await waitFor(() =>
        expect(screen.getByTestId('error')).not.toHaveTextContent(''),
      );
      expect(screen.getByTestId('open')).toHaveTextContent('true');
    });

    it('does not refetch the list', async () => {
      await rejectAfterOpen();

      await waitFor(() =>
        expect(screen.getByTestId('error')).not.toHaveTextContent(''),
      );
      expect(onChanged).not.toHaveBeenCalled();
    });
  });

  it('submits nothing while nothing is selected', async () => {
    respondWith(accountsLoaded);
    renderProbe();
    await open();

    userEvent.click(screen.getByRole('button', { name: 'submit' }));

    expect(global.fetch).toHaveBeenCalledTimes(1);
  });

  it('closes without acting', async () => {
    respondWith(accountsLoaded);
    renderProbe();
    await open();

    userEvent.click(screen.getByRole('button', { name: 'close' }));

    expect(screen.getByTestId('open')).toHaveTextContent('false');
    expect(screen.getByTestId('user')).toHaveTextContent('');
  });

  // The modal stays mounted between openings, so a fetch that settles after a
  // later open would otherwise paint one user's accounts under another user's
  // name — and submitting would revoke access the admin never chose.
  describe('an open that settles after a later one', () => {
    const deferred = () => {
      let settle;
      const promise = new Promise((resolve, reject) => {
        settle = { resolve, reject };
      });

      return { promise, ...settle };
    };

    const openBoth = () => {
      const first = deferred();
      const second = deferred();

      global.fetch = jest
        .fn()
        .mockImplementationOnce(() => first.promise)
        .mockImplementationOnce(() => second.promise);

      renderProbe();
      userEvent.click(screen.getByRole('button', { name: 'open remove' }));
      userEvent.click(
        screen.getByRole('button', {
          name: 'open remove for the other user',
        }),
      );

      const respond = (target, accountAccess) =>
        target.resolve({
          ok: true,
          status: 200,
          json: async () => ({ accountAccess }),
        });

      return { first, second, respond };
    };

    it("keeps the later open's accounts", async () => {
      const { first, second, respond } = openBoth();

      respond(second, [OTHER_ACTIVE]);
      await waitFor(() =>
        expect(screen.getByTestId('options')).toHaveTextContent('3'),
      );

      respond(first, [ACTIVE]);
      await waitFor(() => expect(global.fetch).toHaveBeenCalledTimes(2));

      expect(screen.getByTestId('user')).toHaveTextContent('8');
      expect(screen.getByTestId('options')).toHaveTextContent('3');
    });

    it('keeps an earlier failure off the later open', async () => {
      const { first, second, respond } = openBoth();

      respond(second, [OTHER_ACTIVE]);
      await waitFor(() =>
        expect(screen.getByTestId('options')).toHaveTextContent('3'),
      );

      first.reject(new Error('nope'));
      await waitFor(() => expect(global.fetch).toHaveBeenCalledTimes(2));

      expect(screen.getByTestId('error')).toHaveTextContent('');
    });

    it('writes nothing to a dialog that was closed first', async () => {
      const first = deferred();

      global.fetch = jest.fn().mockImplementationOnce(() => first.promise);
      renderProbe();
      userEvent.click(screen.getByRole('button', { name: 'open remove' }));
      userEvent.click(screen.getByRole('button', { name: 'close' }));

      first.resolve({
        ok: true,
        status: 200,
        json: async () => ({ accountAccess: [ACTIVE] }),
      });
      await waitFor(() => expect(global.fetch).toHaveBeenCalledTimes(1));

      expect(screen.getByTestId('open')).toHaveTextContent('false');
      expect(screen.getByTestId('options')).toHaveTextContent('');
    });
  });
});
